import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Building2, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Plus, 
  Lock, 
  Unlock, 
  Settings, 
  Search, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Sliders,
  Sparkles,
  Layers,
  Clock,
  Check,
  X,
  UserPlus,
  Mail,
  Phone,
  Image as ImageIcon,
  Save,
  Gift,
  Coins
} from 'lucide-react';
import { 
  CashbackCompany, 
  CashbackRequest, 
  CashbackTransaction,
  CashbackMerchantAccessRequest,
  CashbackBannerConfig
} from './CashbackTypes';
import { SiteSettings } from '../../types';

interface CashbackAdminPanelProps {
  companies: CashbackCompany[];
  requests: CashbackRequest[];
  transactions: CashbackTransaction[];
  merchantRequests?: CashbackMerchantAccessRequest[];
  bannerConfig?: CashbackBannerConfig;
  settings?: SiteSettings;
  onUpdateSettings?: (newSettings: SiteSettings) => void;
  onUpdateBannerConfig?: (newConfig: CashbackBannerConfig) => void;
  onToggleCompanyStatus: (companyId: string) => void;
  onUpdateCompanyPlan: (companyId: string, plan: 'free' | 'pro' | 'enterprise') => void;
  onToggleCrossCashback: (companyId: string) => void;
  onAddCompany: (newCompany: any) => void;
  onApproveMerchantRequest?: (requestId: string) => void;
  onRejectMerchantRequest?: (requestId: string) => void;
  onGrantManualAccess?: (userName: string, userEmail: string, companyName: string, category: string) => void;
}

export const CashbackAdminPanel: React.FC<CashbackAdminPanelProps> = ({
  companies,
  requests,
  transactions,
  merchantRequests = [],
  bannerConfig,
  settings,
  onUpdateSettings,
  onUpdateBannerConfig,
  onToggleCompanyStatus,
  onUpdateCompanyPlan,
  onToggleCrossCashback,
  onAddCompany,
  onApproveMerchantRequest,
  onRejectMerchantRequest,
  onGrantManualAccess
}) => {
  const [activeTab, setActiveTab] = useState<'companies' | 'requests' | 'banner' | 'settings'>('companies');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  // Module Enable/Disable State
  const [isModuleEnabled, setIsModuleEnabled] = useState(settings?.enableCashback !== false);
  const [pageTitle, setPageTitle] = useState(settings?.cashbackPageTitle || 'Clube de Cashback Multi-Empresas');
  const [pageSubtitle, setPageSubtitle] = useState(settings?.cashbackPageSubtitle || 'Receba dinheiro de volta em compras nos estabelecimentos parceiros credenciados.');
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  // Banner Form State
  const [bannerBadge, setBannerBadge] = useState(bannerConfig?.badge_text || 'Clube de Cashback Multi-Empresas');
  const [bannerTitle, setBannerTitle] = useState(bannerConfig?.title || 'Receba Dinheiro de Volta em Cada Compra');
  const [bannerSubtitle, setBannerSubtitle] = useState(bannerConfig?.subtitle || 'Envie a nota fiscal das suas compras nos estabelecimentos parceiros e acumule saldo real na sua carteira digital para resgatar onde quiser!');
  const [bannerImageUrl, setBannerImageUrl] = useState(bannerConfig?.banner_image_url || '/images/happy_cashback_banner.jpg');
  const [bannerCtaText, setBannerCtaText] = useState(bannerConfig?.cta_button_text || 'Abrir Minha Carteira & Enviar Nota');
  const [bannerCtaAction, setBannerCtaAction] = useState<'wallet' | 'register' | 'directory'>(bannerConfig?.cta_button_action || 'wallet');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // New Company Form State
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyCategory, setNewCompanyCategory] = useState('Alimentação & Gastronomia');
  const [newCompanyCity, setNewCompanyCity] = useState('Ribeirão Preto - SP');
  const [newCompanyPercent, setNewCompanyPercent] = useState(10);
  const [newCompanyPlan, setNewCompanyPlan] = useState<'free' | 'pro' | 'enterprise'>('pro');

  // Manual Grant Access State
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualCompanyName, setManualCompanyName] = useState('');
  const [manualCategory, setManualCategory] = useState('Alimentação & Gastronomia');

  // Stats Calculations
  const activeCompanies = companies.filter(c => c.status === 'active');
  const totalVolume = requests.filter(r => r.status === 'approved').reduce((acc, r) => acc + r.purchase_amount, 0);
  const totalCashbackGranted = requests.filter(r => r.status === 'approved').reduce((acc, r) => acc + r.calculated_cashback, 0);
  const pendingRequestsCount = merchantRequests.filter(r => r.status === 'pending').length;

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddCompanySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompanyName) return;

    onAddCompany({
      id: `emp-${Date.now()}`,
      name: newCompanyName,
      category: newCompanyCategory,
      logo_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop',
      city_state: newCompanyCity,
      status: 'active',
      plan: newCompanyPlan,
      cross_cashback_enabled: true,
      created_at: new Date().toISOString().split('T')[0],
      settings: {
        type: 'percent',
        default_percent: newCompanyPercent,
        fixed_amount: 0,
        min_purchase: 20,
        max_cashback_per_purchase: 100,
        validity_days: 60
      }
    });

    setIsAddModalOpen(false);
    setNewCompanyName('');
    alert('Nova Empresa cadastrada no SaaS com sucesso!');
  };

  const handleManualGrantSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCompanyName || !manualName) return;

    if (onGrantManualAccess) {
      onGrantManualAccess(manualName, manualEmail, manualCompanyName, manualCategory);
    }
    setIsManualModalOpen(false);
    setManualName('');
    setManualEmail('');
    setManualCompanyName('');
  };

  return (
    <div className="w-full space-y-6">
      {/* ADMIN HERO METRICS */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl border border-slate-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 text-xs font-black uppercase tracking-widest rounded-full border border-indigo-500/30 flex items-center gap-1.5 w-max mb-2">
              <ShieldCheck size={14} /> Painel Administrador Geral SaaS
            </span>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              Gestão Global do Ecossistema de Cashback
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Controle multi-tenant, aprovações de empresas, atribuição manual e auditoria
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* QUICK MODULE TOGGLE */}
            {onUpdateSettings && settings && (
              <button
                onClick={async () => {
                  const nextVal = !isModuleEnabled;
                  setIsModuleEnabled(nextVal);
                  await onUpdateSettings({ ...settings, enableCashback: nextVal });
                  setSettingsSuccess(true);
                  setTimeout(() => setSettingsSuccess(false), 3000);
                }}
                className={`px-3 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer border ${
                  isModuleEnabled
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30'
                    : 'bg-rose-500/20 text-rose-300 border-rose-500/40 hover:bg-rose-500/30'
                }`}
                title="Clique para Ativar ou Desativar o Cashback em todo o site"
              >
                <Coins size={15} />
                Módulo no Site: <span className="underline font-black">{isModuleEnabled ? 'ATIVO' : 'DESATIVADO'}</span>
              </button>
            )}

            <button
              onClick={() => setIsManualModalOpen(true)}
              className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2"
            >
              <UserPlus size={16} /> Atribuir Acesso Manual
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Cadastrar Empresa
            </button>
          </div>
        </div>

        {/* METRICS ROW */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-slate-800">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Empresas Cadastradas</span>
            <span className="text-2xl font-black text-white">{companies.length}</span>
            <span className="text-[10px] text-emerald-400 block font-bold mt-0.5">{activeCompanies.length} ativas</span>
          </div>

          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Solicitações Pendentes</span>
            <span className="text-2xl font-black text-amber-300">{pendingRequestsCount}</span>
            <span className="text-[10px] text-slate-400 block mt-0.5">Aguardando aprovação</span>
          </div>

          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Volume Transacionado</span>
            <span className="text-2xl font-black text-emerald-400">R$ {totalVolume.toFixed(2)}</span>
            <span className="text-[10px] text-slate-400 block mt-0.5">Em compras com nota</span>
          </div>

          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Cashback Concedido</span>
            <span className="text-2xl font-black text-indigo-300">R$ {totalCashbackGranted.toFixed(2)}</span>
            <span className="text-[10px] text-slate-400 block mt-0.5">Distribuído aos clientes</span>
          </div>
        </div>
      </div>

      {/* TOP TAB SWITCHER */}
      <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200/80 shadow-sm w-max gap-2 flex-wrap">
        <button
          onClick={() => setActiveTab('companies')}
          className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'companies'
              ? 'bg-slate-900 text-white shadow-md'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          <Building2 size={16} /> Empresas Lojistas ({companies.length})
        </button>

        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'requests'
              ? 'bg-amber-500 text-slate-950 shadow-md'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          <Clock size={16} /> Solicitações de Acesso ({merchantRequests.length})
          {pendingRequestsCount > 0 && (
            <span className="bg-rose-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-black animate-pulse">
              {pendingRequestsCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('banner')}
          className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'banner'
              ? 'bg-emerald-600 text-white shadow-md'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          <ImageIcon size={16} /> Configuração do Banner Super Admin
        </button>

        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2 cursor-pointer ${
            activeTab === 'settings'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          <Settings size={16} /> Configurações & Ativação do Módulo
        </button>
      </div>

      {/* TAB CONTENT: COMPANIES */}
      {activeTab === 'companies' && (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
              <Building2 className="text-indigo-600" size={20} /> Empresas Lojistas Multi-Tenant
            </h3>

            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por nome ou categoria..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3 px-4">Empresa</th>
                  <th className="py-3 px-4">Categoria & Cidade</th>
                  <th className="py-3 px-4">Cashback Padrão</th>
                  <th className="py-3 px-4">Plano SaaS</th>
                  <th className="py-3 px-4">Cashback Cruzado</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredCompanies.map(comp => (
                  <tr key={comp.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <img src={comp.logo_url} alt={comp.name} className="w-10 h-10 rounded-xl object-cover border shrink-0" />
                        <div>
                          <span className="font-black text-slate-800 block">{comp.name}</span>
                          <span className="text-[10px] text-slate-400 font-mono">ID: {comp.id}</span>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-slate-600">
                      <span className="font-bold text-slate-800 block">{comp.category}</span>
                      <span className="text-[10px] text-slate-400">{comp.city_state}</span>
                    </td>

                    <td className="py-3.5 px-4 font-black text-emerald-600">
                      {comp.settings.default_percent}%
                    </td>

                    <td className="py-3.5 px-4">
                      <select
                        value={comp.plan}
                        onChange={(e) => onUpdateCompanyPlan(comp.id, e.target.value as any)}
                        className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-800"
                      >
                        <option value="free">FREE</option>
                        <option value="pro">PRO</option>
                        <option value="enterprise">ENTERPRISE</option>
                      </select>
                    </td>

                    <td className="py-3.5 px-4">
                      <button
                        onClick={() => onToggleCrossCashback(comp.id)}
                        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase transition-colors ${
                          comp.cross_cashback_enabled
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {comp.cross_cashback_enabled ? 'Habilitado' : 'Somente Própria'}
                      </button>
                    </td>

                    <td className="py-3.5 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                        comp.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {comp.status === 'active' ? 'Ativa' : 'Bloqueada'}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <button
                        onClick={() => onToggleCompanyStatus(comp.id)}
                        className={`px-3 py-1.5 rounded-xl font-bold text-[10px] uppercase transition-colors ${
                          comp.status === 'active'
                            ? 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                        }`}
                      >
                        {comp.status === 'active' ? 'Bloquear' : 'Desbloquear'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: MERCHANT ACCESS REQUESTS */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <Clock className="text-amber-500" size={20} /> Solicitações de Acesso ao Painel da Empresa
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Usuários comuns que solicitaram a liberação do módulo de Lojista
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3 px-4">Solicitante / Contato</th>
                  <th className="py-3 px-4">Empresa / Categoria</th>
                  <th className="py-3 px-4">CNPJ / CPF</th>
                  <th className="py-3 px-4">Data</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Ação Super Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {merchantRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-400 font-medium">
                      Nenhuma solicitação de acesso de empresa registrada no momento.
                    </td>
                  </tr>
                ) : (
                  merchantRequests.map(req => (
                    <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-4">
                        <div>
                          <span className="font-black text-slate-800 block">{req.user_name}</span>
                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Mail size={10} /> {req.user_email || 'N/A'} | <Phone size={10} /> {req.user_phone}
                          </span>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <span className="font-bold text-indigo-600 block">{req.company_name}</span>
                        <span className="text-[10px] text-slate-400">{req.company_category} - {req.company_city}</span>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 font-mono text-[11px]">
                        {req.cnpj_cpf || 'Não informado'}
                      </td>

                      <td className="py-3.5 px-4 text-slate-400 text-[10px]">
                        {req.created_at}
                      </td>

                      <td className="py-3.5 px-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase inline-flex items-center gap-1 ${
                          req.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                          req.status === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {req.status === 'approved' && <Check size={10} />}
                          {req.status === 'rejected' && <X size={10} />}
                          {req.status === 'pending' && <Clock size={10} />}
                          {req.status === 'pending' ? 'Pendente' : req.status === 'approved' ? 'Aprovado' : 'Recusado'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        {req.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => onRejectMerchantRequest && onRejectMerchantRequest(req.id)}
                              className="px-3 py-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-xl font-bold text-[10px] uppercase transition-colors"
                            >
                              Recusar
                            </button>
                            <button
                              onClick={() => onApproveMerchantRequest && onApproveMerchantRequest(req.id)}
                              className="px-4 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl font-bold text-[10px] uppercase shadow-md transition-colors"
                            >
                              Aprovar
                            </button>
                          </div>
                        )}
                        {req.status === 'approved' && (
                          <span className="text-[10px] text-emerald-600 font-bold">Acesso Concedido</span>
                        )}
                        {req.status === 'rejected' && (
                          <span className="text-[10px] text-rose-500 font-bold">Solicitação Recusada</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: BANNER CONFIGURATION (SUPER ADMIN) */}
      {activeTab === 'banner' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <ImageIcon className="text-emerald-600" size={20} /> Personalizar Banner de Capa & Imagens de Pessoas Felizes
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Altere a imagem de fundo, títulos, textos e botões de chamada para ação exibidos no Clube de Cashback do Automatiza e Vende.
              </p>
            </div>

            {saveSuccess && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-bold text-xs rounded-full flex items-center gap-1.5 animate-fade-in">
                <CheckCircle2 size={16} /> Salvo com sucesso!
              </span>
            )}
          </div>

          {/* LIVE PREVIEW BOX */}
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase text-slate-500 tracking-wider flex items-center gap-1">
              <Sparkles size={14} className="text-amber-500" /> Pré-visualização em Tempo Real do Banner:
            </span>
            <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white shadow-xl border border-emerald-500/20 group">
              <img 
                src={bannerImageUrl} 
                alt="Banner Pessoas Felizes e Empresas" 
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover object-center opacity-40 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>

              <div className="relative z-10 p-6 md:p-8 max-w-xl space-y-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-widest rounded-full backdrop-blur-md">
                  <Coins size={12} className="text-emerald-400 animate-pulse" /> {bannerBadge}
                </span>

                <h1 className="text-xl md:text-3xl font-black tracking-tight leading-tight text-white drop-shadow-md">
                  {bannerTitle}
                </h1>

                <p className="text-xs text-slate-200 font-medium leading-relaxed">
                  {bannerSubtitle}
                </p>

                <div className="pt-2">
                  <button type="button" className="px-5 py-3 bg-emerald-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg flex items-center gap-2">
                    <Gift size={16} /> {bannerCtaText}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* EDIT FORM */}
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              if (onUpdateBannerConfig) {
                onUpdateBannerConfig({
                  badge_text: bannerBadge,
                  title: bannerTitle,
                  subtitle: bannerSubtitle,
                  banner_image_url: bannerImageUrl,
                  cta_button_text: bannerCtaText,
                  cta_button_action: bannerCtaAction
                });
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
              }
            }}
            className="space-y-6 pt-4 border-t border-slate-100"
          >
            {/* PRESET BANNER IMAGES OF HAPPY PEOPLE / REAL STORES */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase text-slate-700">
                Selecione uma Foto Real de Pessoas Felizes e Empresas Parceiras:
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    url: '/images/happy_cashback_banner.jpg',
                    label: 'Foto Gerada: Pessoas Felizes & Lojas'
                  },
                  {
                    url: 'https://images.unsplash.com/photo-1556742049-0a670f4a4591?w=1200&h=600&fit=crop',
                    label: 'Clientes Sorridentes no Balcão'
                  },
                  {
                    url: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=1200&h=600&fit=crop',
                    label: 'Amigos e Família Comemorando'
                  },
                  {
                    url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=600&fit=crop',
                    label: 'Compras com Desconto & Sacolas'
                  }
                ].map((imgItem, idx) => (
                  <button
                    type="button"
                    key={idx}
                    onClick={() => setBannerImageUrl(imgItem.url)}
                    className={`relative rounded-2xl overflow-hidden border-2 text-left transition-all group cursor-pointer h-28 ${
                      bannerImageUrl === imgItem.url
                        ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <img 
                      src={imgItem.url} 
                      alt={imgItem.label} 
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent p-2 flex items-end">
                      <span className="text-[10px] font-bold text-white leading-tight">
                        {imgItem.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* CUSTOM IMAGE URL INPUT */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                URL Personalizada da Imagem do Banner (ou cole o link de uma imagem externa)
              </label>
              <input
                type="text"
                value={bannerImageUrl}
                onChange={(e) => setBannerImageUrl(e.target.value)}
                placeholder="Ex: https://sua-imagem.com/banner-pessoas-felizes.jpg"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
              />
            </div>

            {/* BADGE AND TITLE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Texto da Tag Superior (Badge)
                </label>
                <input
                  type="text"
                  required
                  value={bannerBadge}
                  onChange={(e) => setBannerBadge(e.target.value)}
                  placeholder="Ex: Clube de Cashback Multi-Empresas"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Título Principal do Banner
                </label>
                <input
                  type="text"
                  required
                  value={bannerTitle}
                  onChange={(e) => setBannerTitle(e.target.value)}
                  placeholder="Ex: Receba Dinheiro de Volta em Cada Compra"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                />
              </div>
            </div>

            {/* SUBTITLE */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                Subtítulo / Descrição Explicativa
              </label>
              <textarea
                rows={3}
                required
                value={bannerSubtitle}
                onChange={(e) => setBannerSubtitle(e.target.value)}
                placeholder="Ex: Envie a nota fiscal das suas compras..."
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 resize-none"
              />
            </div>

            {/* CTA BUTTON CONFIG */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Texto do Botão Principal (CTA)
                </label>
                <input
                  type="text"
                  required
                  value={bannerCtaText}
                  onChange={(e) => setBannerCtaText(e.target.value)}
                  placeholder="Ex: Abrir Minha Carteira & Enviar Nota"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                  Ação do Botão
                </label>
                <select
                  value={bannerCtaAction}
                  onChange={(e) => setBannerCtaAction(e.target.value as any)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                >
                  <option value="wallet">Abrir Carteira & Enviar Nota Fiscal</option>
                  <option value="directory">Navegar pelas Lojas Parceiras</option>
                  <option value="register">Solicitar Login / Cadastro</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
            >
              <Save size={18} /> Salvar Alterações do Banner no Super Admin
            </button>
          </form>
        </div>
      )}

      {/* TAB CONTENT: GLOBAL SETTINGS & MODULE ACTIVATION */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-sm space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <Settings className="text-purple-600" size={20} /> Configurações Gerais & Ativação do Módulo de Cashback
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Controle a visibilidade global do Cashback no site, links no menu e títulos da página pública.
              </p>
            </div>

            {settingsSuccess && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-bold text-xs rounded-full flex items-center gap-1.5 animate-fade-in">
                <CheckCircle2 size={16} /> Configurações salvas com sucesso!
              </span>
            )}
          </div>

          <div className="space-y-6">
            {/* MASTER ACTIVATION CARD */}
            <div className="bg-gradient-to-br from-slate-50 to-purple-50/50 rounded-3xl p-6 border border-purple-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                    isModuleEnabled ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-rose-100 text-rose-800 border border-rose-200'
                  }`}>
                    {isModuleEnabled ? 'Módulo Ativo no Site' : 'Módulo Desativado'}
                  </span>
                </div>
                <h4 className="text-base font-black text-slate-900">
                  Ativar Módulo de Cashback no Site
                </h4>
                <p className="text-xs text-slate-600 max-w-xl leading-relaxed">
                  Quando <strong>ativado</strong>, exibe o botão <strong>CASHBACK</strong> no cabeçalho e rodapé do site, além de permitir o acesso público ao catálogo de lojas, envio de notas fiscais e carteira digital de clientes. Ao <strong>desativar</strong>, os menus públicos são ocultados imediatamente.
                </p>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsModuleEnabled(!isModuleEnabled)}
                  className={`w-16 h-9 rounded-full relative transition-all shadow-inner cursor-pointer ${
                    isModuleEnabled ? 'bg-emerald-600' : 'bg-slate-300'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-7 h-7 bg-white rounded-full transition-all shadow-md flex items-center justify-center ${
                      isModuleEnabled ? 'left-8 text-emerald-600' : 'left-1 text-slate-400'
                    }`}
                  >
                    {isModuleEnabled ? <Check size={14} /> : <X size={14} />}
                  </div>
                </button>
              </div>
            </div>

            {/* PAGE TITLE & CUSTOMIZATION FORM */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (onUpdateSettings && settings) {
                  await onUpdateSettings({
                    ...settings,
                    enableCashback: isModuleEnabled,
                    cashbackPageTitle: pageTitle,
                    cashbackPageSubtitle: pageSubtitle
                  });
                  setSettingsSuccess(true);
                  setTimeout(() => setSettingsSuccess(false), 3000);
                }
              }}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    Título da Página Pública de Cashback
                  </label>
                  <input
                    type="text"
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                    placeholder="Ex: Clube de Cashback Multi-Empresas"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-purple-500 focus:bg-white transition-all"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">Título principal exibido aos visitantes</span>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
                    Subtítulo / Descrição da Página
                  </label>
                  <input
                    type="text"
                    value={pageSubtitle}
                    onChange={(e) => setPageSubtitle(e.target.value)}
                    placeholder="Ex: Compre nas empresas parceiras e ganhe dinheiro de volta"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-purple-500 focus:bg-white transition-all"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">Texto explicativo exibido abaixo do título</span>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="px-6 py-3.5 bg-purple-600 hover:bg-purple-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                >
                  <Save size={18} /> Salvar Configurações de Cashback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD COMPANY MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <Building2 className="text-indigo-600" size={20} /> Cadastrar Empresa no SaaS
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 text-lg font-bold">✕</button>
            </div>

            <form onSubmit={handleAddCompanySubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Nome da Empresa *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Supermercado Primavera"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Categoria *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Mercado & Hortifruti"
                  value={newCompanyCategory}
                  onChange={(e) => setNewCompanyCategory(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Cashback Padrão (%)</label>
                  <input
                    type="number"
                    value={newCompanyPercent}
                    onChange={(e) => setNewCompanyPercent(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Plano SaaS</label>
                  <select
                    value={newCompanyPlan}
                    onChange={(e) => setNewCompanyPlan(e.target.value as any)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                  >
                    <option value="free">FREE</option>
                    <option value="pro">PRO</option>
                    <option value="enterprise">ENTERPRISE</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg transition-all"
              >
                Criar Empresa e Liberar Acesso
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MANUAL GRANT ACCESS MODAL */}
      {isManualModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <UserPlus className="text-indigo-600" size={20} /> Atribuir Acesso de Empresa Manualmente
              </h3>
              <button onClick={() => setIsManualModalOpen(false)} className="text-slate-400 text-lg font-bold">✕</button>
            </div>

            <form onSubmit={handleManualGrantSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Nome do Usuário *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Carlos Silva"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">E-mail do Usuário</label>
                <input
                  type="email"
                  placeholder="carlos@exemplo.com"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Nome da Empresa do Lojista *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Padaria Bella Vista"
                  value={manualCompanyName}
                  onChange={(e) => setManualCompanyName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 mb-1">Categoria</label>
                <select
                  value={manualCategory}
                  onChange={(e) => setManualCategory(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                >
                  <option value="Alimentação & Gastronomia">Alimentação & Gastronomia</option>
                  <option value="Supermercado & Conveniência">Supermercado & Conveniência</option>
                  <option value="Vestuário & Moda">Vestuário & Moda</option>
                  <option value="Serviços Gerais">Serviços Gerais</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg transition-all"
              >
                Conceder Acesso de Empresa Agora
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
